import { forwardRef } from 'react';
import { cn } from '../utils/cn';

/**
 * One inline SVG set for the whole product. Keys match the `icon` values in the
 * contracts' NAVIGATION table and the `iconToken` fields on the read models, so
 * a read model can name an icon without the app maintaining a mapping table.
 */
const paths = {
  // Navigation (routes.ts NAVIGATION.icon)
  gauge: 'M12 13a1 1 0 100-2 1 1 0 000 2zM12.7 11.3L17 7M3.5 18a9 9 0 1117 0',
  hierarchy: 'M12 3v4M6 21v-4M18 21v-4M6 17h12v-6H6zM9 7h6V3H9zM12 7v4M4 21h4M16 21h4',
  // Four corners pushing out / pulling in. A panel that can fill the screen
  // needs a glyph that says so; `external` reads as "opens elsewhere".
  maximize: 'M9 4H4v5M15 4h5v5M15 20h5v-5M9 20H4v-5',
  minimize: 'M4 9h5V4M20 9h-5V4M20 15h-5v5M4 15h5v5',
  expand: 'M12 5v14M5 12h14',
  collapse: 'M5 12h14',
  compass: 'M12 21a9 9 0 100-18 9 9 0 000 18zM15.5 8.5l-2 5-5 2 2-5z',
  cube: 'M12 3l8 4.5v9L12 21l-8-4.5v-9zM12 12l8-4.5M12 12v9M12 12L4 7.5',
  flow: 'M4 6h6M14 6h6M4 18h6M14 18h6M10 6a2 2 0 104 0 2 2 0 10-4 0M10 18a2 2 0 104 0 2 2 0 10-4 0M12 8v8',
  grid: 'M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z',
  apps: 'M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h6v6h-6z',
  activity: 'M3 12h4l3 8 4-16 3 8h4',
  coins:
    'M9 12a6 3 0 1012 0 6 3 0 10-12 0M21 12v5c0 1.7-2.7 3-6 3s-6-1.3-6-3v-5M3 7a6 3 0 1012 0 6 3 0 10-12 0M3 7v5c0 1.4 1.9 2.6 4.5 2.9',
  people:
    'M16 20v-1.5A3.5 3.5 0 0012.5 15h-5A3.5 3.5 0 004 18.5V20M10 11a3.5 3.5 0 100-7 3.5 3.5 0 000 7M20 20v-1.5a3.5 3.5 0 00-2.6-3.4M15.5 4.2a3.5 3.5 0 010 6.6',
  truck:
    'M3 6h11v10H3zM14 9h4l3 3.5V16h-7zM7.5 19a1.75 1.75 0 100-3.5 1.75 1.75 0 000 3.5M17.5 19a1.75 1.75 0 100-3.5 1.75 1.75 0 000 3.5',
  contract: 'M6 3h8l4 4v14H6zM14 3v4h4M9 12h6M9 16h6M9 8h2',
  sliders:
    'M4 7h8M16 7h4M4 17h4M12 17h8M14 7a2 2 0 104 0 2 2 0 10-4 0M6 17a2 2 0 104 0 2 2 0 10-4 0',
  upload: 'M12 16V4M8 8l4-4 4 4M4 16v3a1 1 0 001 1h14a1 1 0 001-1v-3',
  plug: 'M9 3v5M15 3v5M6 8h12v3a6 6 0 01-6 6 6 6 0 01-6-6zM12 17v4',
  lineage:
    'M5 6a2 2 0 104 0 2 2 0 10-4 0M15 12a2 2 0 104 0 2 2 0 10-4 0M5 18a2 2 0 104 0 2 2 0 10-4 0M9 6h3a2 2 0 012 2v0M15 12h-3a2 2 0 00-2 2v2M14 14v0',
  shield: 'M12 3l7 3v6c0 4.2-2.8 7.6-7 9-4.2-1.4-7-4.8-7-9V6zM9.5 12l1.8 1.8 3.4-3.6',

  // Utility
  search: 'M11 19a8 8 0 100-16 8 8 0 000 16zM21 21l-4.3-4.3',
  bell: 'M18 16V11a6 6 0 10-12 0v5l-2 2h16zM10 21h4',
  user: 'M20 21v-2a5 5 0 00-5-5H9a5 5 0 00-5 5v2M12 11a4 4 0 100-8 4 4 0 000 8',
  'chevron-right': 'M9 5l7 7-7 7',
  'chevron-left': 'M15 5l-7 7 7 7',
  'chevron-down': 'M5 9l7 7 7-7',
  'chevron-up': 'M19 15l-7-7-7 7',
  external: 'M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 01-1 1H5a1 1 0 01-1-1V7a1 1 0 011-1h5',
  download: 'M12 4v12M8 12l4 4 4-4M4 20h16',
  filter: 'M3 5h18l-7 8v6l-4 2v-8z',
  plus: 'M12 5v14M5 12h14',
  minus: 'M5 12h14',
  check: 'M5 12.5l4.5 4.5L19 7',
  alert: 'M12 3l9 16H3zM12 9v5M12 17.2v.1',
  info: 'M12 21a9 9 0 100-18 9 9 0 000 18zM12 11v6M12 7.8v.1',
  x: 'M6 6l12 12M18 6L6 18',

  // Status, data and workflow
  'arrow-up': 'M12 19V5M6 11l6-6 6 6',
  'arrow-down': 'M12 5v14M6 13l6 6 6-6',
  'arrow-right': 'M5 12h14M13 6l6 6-6 6',
  'trend-flat': 'M4 12h16',
  clock: 'M12 21a9 9 0 100-18 9 9 0 000 18zM12 7v5l3 2',
  calendar: 'M4 6h16v14H4zM4 10h16M8 3v4M16 3v4',
  database:
    'M5 6c0-1.7 3.1-3 7-3s7 1.3 7 3-3.1 3-7 3-7-1.3-7-3zM5 6v12c0 1.7 3.1 3 7 3s7-1.3 7-3V6M5 12c0 1.7 3.1 3 7 3s7-1.3 7-3',
  refresh: 'M20 11a8 8 0 10-1.8 6M20 4v6h-6',
  lock: 'M6 11h12v9H6zM9 11V8a3 3 0 116 0v3M12 15v2',
  eye: 'M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12zM12 15a3 3 0 100-6 3 3 0 000 6',
  'eye-off':
    'M4 4l16 16M10 6a8.6 8.6 0 012-.2c6 0 9.5 6.2 9.5 6.2a15 15 0 01-3.2 3.9M6.4 7.9A15 15 0 002.5 12S6 18.2 12 18.2c1.2 0 2.2-.2 3.2-.6M10 10a3 3 0 004 4',
  edit: 'M4 20h4l10-10-4-4L4 16zM14 6l4 4',
  trash: 'M5 7h14M9 7V5h6v2M7 7l1 13h8l1-13M11 11v6M13 11v6',
  file: 'M7 3h7l4 4v14H7zM14 3v4h4M10 13h6M10 17h6',
  link: 'M10 14a4 4 0 006 .5l2-2a4 4 0 00-5.7-5.7l-1 1M14 10a4 4 0 00-6-.5l-2 2a4 4 0 005.7 5.7l1-1',
  star: 'M12 4l2.5 5.2 5.5.8-4 4 1 5.6-5-2.7-5 2.7 1-5.6-4-4 5.5-.8z',
  help: 'M12 21a9 9 0 100-18 9 9 0 000 18zM9.5 9.5a2.6 2.6 0 015 1c0 1.7-2.5 2-2.5 3.5M12 17.2v.1',
  more: 'M6 12h.01M12 12h.01M18 12h.01',
  columns: 'M4 4h16v16H4zM10 4v16M16 4v16',
  sort: 'M8 6v12M8 18l-3-3M8 6l3 3M16 18V6M16 6l3 3M16 18l-3-3',
  'sort-asc': 'M6 15l4 4 4-4M10 19V5',
  'sort-desc': 'M6 9l4-4 4 4M10 5v14',
  drag: 'M9 6h.01M9 12h.01M9 18h.01M15 6h.01M15 12h.01M15 18h.01',
  'check-circle': 'M12 21a9 9 0 100-18 9 9 0 000 18zM8.5 12.2l2.4 2.4 4.6-5',
  'x-circle': 'M12 21a9 9 0 100-18 9 9 0 000 18zM9.5 9.5l5 5M14.5 9.5l-5 5',
  'alert-circle': 'M12 21a9 9 0 100-18 9 9 0 000 18zM12 8v5M12 15.8v.1',
  circle: 'M12 20a8 8 0 100-16 8 8 0 000 16',
  dot: 'M12 15a3 3 0 100-6 3 3 0 000 6',
  pause: 'M9 5v14M15 5v14',
  play: 'M7 4l12 8-12 8z',
  spinner: 'M12 3a9 9 0 019 9',
  flag: 'M6 21V4h12l-3 4 3 4H6',
  building: 'M4 21V6l7-3v18M11 21h9V10h-9M14 13h3M14 17h3M7 9h1M7 13h1M7 17h1',
  target:
    'M12 21a9 9 0 100-18 9 9 0 000 18zM12 17a5 5 0 100-10 5 5 0 000 10zM12 13a1 1 0 100-2 1 1 0 000 2',
  sparkle:
    'M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8zM18 16l.9 2.1L21 19l-2.1.9L18 22l-.9-2.1L15 19l2.1-.9z',
} as const;

export type IconName = keyof typeof paths;

export const iconNames = Object.keys(paths) as IconName[];

export interface IconProps extends Omit<React.SVGProps<SVGSVGElement>, 'name' | 'ref'> {
  name: IconName;
  size?: number;
  /**
   * Supply a label when the icon is the only carrier of meaning; omit it when
   * adjacent text already says the same thing and the icon is decorative.
   */
  label?: string;
}

export const Icon = forwardRef<SVGSVGElement, IconProps>(function Icon(
  { name, size = 16, label, className, strokeWidth = 1.75, ...rest },
  ref,
) {
  const d = paths[name];
  return (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cn('shrink-0', className)}
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      focusable="false"
      {...rest}
    >
      {label ? <title>{label}</title> : null}
      <path d={d} />
    </svg>
  );
});

/** Resolve an untrusted `iconToken` from a read model to a known icon. */
export function resolveIconToken(token: string | null | undefined, fallback: IconName = 'dot') {
  if (token && token in paths) return token as IconName;
  return fallback;
}
